<template>
    <div>
        <ServerError v-if="ServerError" />
        <PageLoader :storage="appLoading" />
        <v-snackbar v-model="showSnackBar" :color="appColor" right :timeout="timeout">
            <v-layout wrap justify-center>
                <v-flex text-left class="align-self-center">
                    <span style="color: #fff">{{ msg }}</span>
                </v-flex>
                <v-flex text-right>
                    <v-btn small :ripple="false" text @click="showSnackBar = false">
                        <v-icon style="color: #fff">mdi-close</v-icon>
                    </v-btn>
                </v-flex>
            </v-layout>
        </v-snackbar>

        <v-layout wrap justify-start pt-3 pl-2 pr-5>
            <v-flex align-self-center xs12 text-left>
                <v-btn icon @click="$router.go(-1)" class="mr-2">
                    <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
                <span class="itemHeading1">Fencing Details</span>
            </v-flex>
        </v-layout>

        <v-layout wrap justify-center pt-4>
            <v-flex xs12 md4 pa-3>
                <v-card v-if="fencingData" class="pa-4">
                    <v-card-title class="itemHeading">
                        {{ fencingData.name || 'Fencing Details' }}
                    </v-card-title>
                    <v-card-text>
                        <v-layout wrap>
                            <v-flex xs12 py-2>
                                <span class="itemKey">Type: </span>
                                <span class="itemValue text-capitalize">{{ fencingData.type }}</span>
                            </v-flex>
                            <v-flex xs12 py-2>
                                <span class="itemKey">Status: </span>
                                <v-chip small :color="fencingData.status === 'active' ? 'success' : 'grey'"
                                    text-color="white" class="text-capitalize">
                                    {{ fencingData.status }}
                                </v-chip>
                            </v-flex>
                            <v-flex xs12 py-2>
                                <span class="itemKey">Area (Hectares): </span>
                                <span class="itemValue">{{ fencingData.areaHectares ?
                                    fencingData.areaHectares.toFixed(4) : '0' }}</span>
                            </v-flex>
                            <v-flex xs12 py-2>
                                <span class="itemKey">Added By: </span>
                                <span class="itemValue">{{ fencingData.addedby.name }}</span>
                            </v-flex>
                            <v-flex xs12 py-2>
                                <span class="itemKey">Entry Date: </span>
                                <span class="itemValue">{{ formatDate(fencingData.entryOn) }}</span>
                            </v-flex>
                        </v-layout>
                    </v-card-text>
                </v-card>
            </v-flex>

            <v-flex xs12 md8 pa-3>
                <v-card height="400px">
                    <l-map v-if="mapReady" :zoom="zoom" :center="center" :scroll-wheel-zoom="false"
                        style="height: 100%; width: 100%">
                        <l-tile-layer :url="url" :attribution="attribution"></l-tile-layer>

                        <!-- If it's a Point -->
                        <l-marker
                            v-if="fencingData && fencingData.coordinates && fencingData.coordinates.type === 'Point'"
                            :lat-lng="[fencingData.coordinates.coordinates[1], fencingData.coordinates.coordinates[0]]">
                        </l-marker>

                        <!-- If it's a Polygon / LineString -->
                        <template
                            v-if="fencingData && fencingData.coordinates && (fencingData.coordinates.type === 'Polygon' || fencingData.coordinates.type === 'LineString')">
                            <l-polygon v-if="fencingData.coordinates.type === 'Polygon'" :lat-lngs="polygonLatLngs"
                                :color="appColor" :weight="5" :opacity="1" :fillOpacity="0.3">
                            </l-polygon>
                            <l-polyline v-else :lat-lngs="polygonLatLngs" :color="appColor" :weight="5" :opacity="1">
                            </l-polyline>

                            <!-- Outer glow for vertices -->
                            <l-circle-marker v-for="(latlng, index) in polygonLatLngs" :key="'glow-' + index"
                                :lat-lng="latlng" :radius="10" :stroke="false" :fillColor="appColor" :fillOpacity="0.4">
                            </l-circle-marker>

                            <!-- Main dot for vertices -->
                            <l-circle-marker v-for="(latlng, index) in polygonLatLngs" :key="'marker-' + index"
                                :lat-lng="latlng" :radius="6" :color="'#FFF'" :weight="2" :fillColor="appColor"
                                :fillOpacity="1">
                            </l-circle-marker>
                        </template>
                    </l-map>
                    <v-layout v-else-if="!appLoading" fill-height align-center justify-center>
                        <span class="itemform">Map Loading...</span>
                    </v-layout>
                </v-card>
            </v-flex>
        </v-layout>
    </div>
</template>

<script>
import axios from "axios";
import { LMap, LTileLayer, LMarker, LPolygon, LCircleMarker, LPolyline } from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
    components: {
        LMap,
        LTileLayer,
        LMarker,
        LPolygon,
        LCircleMarker,
        LPolyline,
    },
    data() {
        return {
            appLoading: false,
            ServerError: false,
            showSnackBar: false,
            timeout: 3000,
            msg: null,
            fencingData: null,
            mapReady: false,
            url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
            attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
            zoom: 13,
            center: [9.5486, 76.8719], // Default center
        };
    },
    computed: {
        id() {
            return this.$route.query.id;
        },
        polygonLatLngs() {
            if (this.fencingData && this.fencingData.coordinates) {
                const type = this.fencingData.coordinates.type;
                const coords = this.fencingData.coordinates.coordinates;
                if (type === 'Polygon') {
                    // GeoJSON Polygon coordinates are [ [ [ln, lt], [ln, lt] ] ]
                    return coords[0].map(coord => [coord[1], coord[0]]);
                } else if (type === 'LineString') {
                    // GeoJSON LineString coordinates are [ [ln, lt], [ln, lt] ]
                    return coords.map(coord => [coord[1], coord[0]]);
                }
            }
            return [];
        }
    },
    beforeMount() {
        if (this.id) {
            this.getData();
        } else {
            this.$router.push("/Admin/fencing-list");
        }
    },
    methods: {
        getData() {
            this.appLoading = true;
            axios({
                method: "GET",
                url: "/fencing/view/" + this.id,
                headers: {
                    "x-auth-token": localStorage.getItem("token"),
                }
            })
                .then((response) => {
                    this.appLoading = false;
                    if (response.data.status == true) {
                        this.fencingData = response.data.data;
                        this.updateMapCenter();
                    } else {
                        this.msg = response.data.msg;
                        this.showSnackBar = true;
                    }
                })
                .catch((err) => {
                    this.appLoading = false;
                    this.ServerError = true;
                    console.log(err);
                });
        },
        updateMapCenter() {
            if (this.fencingData && this.fencingData.coordinates) {
                const type = this.fencingData.coordinates.type;
                const coords = this.fencingData.coordinates.coordinates;

                if (type === 'Point') {
                    this.center = [coords[1], coords[0]];
                    this.zoom = 15;
                    this.mapReady = true;
                } else {
                    // For Polygon and LineString, use the first coordinate point to center
                    const firstPoint = type === 'Polygon' ? coords[0][0] : coords[0];
                    this.center = [firstPoint[1], firstPoint[0]];
                    this.zoom = 16;
                    this.mapReady = true;
                }
            }
        },
        formatDate(dateString) {
            if (!dateString) return "-";
            const date = new Date(dateString);
            return date.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            });
        },
    },
};
</script>

<style scoped>
.itemHeading1 {
    font-family: poppinssemibold;
    font-size: 24px;
    color: #000;
}

.itemHeading {
    font-family: poppinssemibold;
    font-size: 18px;
    color: #000;
}

.itemKey {
    font-family: poppinssemibold;
    font-size: 14px;
    color: #444;
}

.itemValue {
    font-family: poppinsmedium;
    font-size: 14px;
    color: #000;
}

.itemform {
    font-family: poppinsmedium;
    font-size: 14px;
}
</style>
